/**
 * Equipment-detail extractors — turn raw `getPlantEquipmentDetail`
 * cypher rows into typed `PlantEquipmentProperty[]`, `PlantUdtInstance[]`,
 * and the tag-merging helpers the equipment detail page consumes.
 *
 * Moved here from `ontology-api.ts` (private helpers) so the endpoint
 * stays a thin shell of `transformResponse` calls.
 */

import { extractNode, extractRawNode } from '../graph-row'
import { extractPlantMeta } from './provenance'
import type {
  PlantEquipmentProperty,
  PlantEquipmentPropertyKind,
  PlantTag,
  PlantUdtInstance,
  PlantUdtMember
} from '../types'

/**
 * Coerce the wire-format `kind` we baked into each per-rel-type
 * `OPTIONAL MATCH … WITH … collect(DISTINCT { …, kind: 'observable' |
 * 'setpoint' | 'property' })` block in `getPlantEquipmentDetail` into
 * our typed enum, with a fall-back to `'property'` for the pre-AI-pass
 * case where everything still lives behind `:EquipmentProperty`. The
 * optional `rawKind` argument lets us upgrade a `:EquipmentProperty`
 * node that already carries `kind: 'observable' | 'setpoint'` into the
 * proper bucket (this is the pattern the AI extractor uses before
 * promoting nodes to `:Observable` / `:Setpoint`).
 */
function classifyEquipmentProperty(
  cypherKind: unknown,
  rawKind: unknown
): PlantEquipmentPropertyKind {
  if (typeof rawKind === 'string') {
    if (rawKind === 'observable') return 'observable'
    if (rawKind === 'setpoint') return 'setpoint'
  }
  if (typeof cypherKind === 'string') {
    if (cypherKind === 'observable') return 'observable'
    if (cypherKind === 'setpoint') return 'setpoint'
  }
  return 'property'
}

/**
 * Concat of the three per-rel-type `collect(DISTINCT { prop, kind, tag })`
 * lists returned by `getPlantEquipmentDetail`. Each row is:
 *
 *   - `{ prop: <Observable|Setpoint|EquipmentProperty>, kind, tag }` —
 *     the spec-mandated indirected shape; the property node carries
 *     unit/range metadata and the `tag` is the live readout.
 *
 * The legacy flat-ingest fallback (`{ prop: null, kind, tag }`) is no
 * longer emitted by the cypher since direct Equipment→Tag edges are
 * forbidden by the SCADA ontology spec — but the synthetic-property
 * branch below is kept defensively in case older graph data still
 * contains the removed shape. When it triggers, we synthesise a
 * property entry off the tag itself: id from `synthetic:<tag-id>`,
 * name from the tag's last path segment, and unit/range pulled off
 * the tag when present.
 *
 * Sentinel `NULL` rows (surfaced when the OPTIONAL MATCH didn't bind
 * anything at all) are filtered upstream by the cypher.
 */
export function extractEquipmentProperties(value: unknown): PlantEquipmentProperty[] {
  if (!Array.isArray(value)) return []
  const out: PlantEquipmentProperty[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const tag = extractNode<PlantTag>(obj['tag'])
    const tagWithLabel = tag ? { ...tag, label: tag.label ?? 'Tag' } : null
    const propRaw = extractRawNode(obj['prop'])

    if (propRaw) {
      const id = typeof propRaw['id'] === 'string' ? (propRaw['id'] as string) : null
      const elementId = typeof propRaw['_id'] === 'string' ? (propRaw['_id'] as string) : id
      if (!id || !elementId) continue
      if (seen.has(id)) continue
      seen.add(id)
      const name = typeof propRaw['name'] === 'string' ? (propRaw['name'] as string) : id
      out.push({
        id,
        _id: elementId,
        name,
        ...extractPlantMeta(propRaw),
        kind: classifyEquipmentProperty(obj['kind'], propRaw['kind']),
        unit: typeof propRaw['unit'] === 'string' ? (propRaw['unit'] as string) : undefined,
        range_low:
          typeof propRaw['range_low'] === 'number' ? (propRaw['range_low'] as number) : undefined,
        range_high:
          typeof propRaw['range_high'] === 'number' ? (propRaw['range_high'] as number) : undefined,
        description:
          typeof propRaw['description'] === 'string'
            ? (propRaw['description'] as string)
            : undefined,
        tag: tagWithLabel
      })
      continue
    }

    if (!tagWithLabel) continue
    const synthId = `synthetic:${tagWithLabel.id}`
    if (seen.has(synthId)) continue
    seen.add(synthId)
    const tagAsRecord = tagWithLabel as unknown as Record<string, unknown>
    out.push({
      id: synthId,
      _id: synthId,
      name: leafTagSegment(tagWithLabel.name) ?? tagWithLabel.name ?? tagWithLabel.id,
      kind: classifyEquipmentProperty(obj['kind'], undefined),
      unit:
        typeof tagAsRecord['unit'] === 'string'
          ? (tagAsRecord['unit'] as string)
          : typeof tagAsRecord['engUnit'] === 'string'
            ? (tagAsRecord['engUnit'] as string)
            : undefined,
      range_low: pickNumeric(tagAsRecord, ['range_low', 'engLow', 'min']),
      range_high: pickNumeric(tagAsRecord, ['range_high', 'engHigh', 'max']),
      description:
        typeof tagAsRecord['description'] === 'string'
          ? (tagAsRecord['description'] as string)
          : undefined,
      tag: tagWithLabel
    })
  }
  return out
}

/** Last `/`-delimited segment of a tag path (e.g. `Plant/PST-201/Flow` → `Flow`). */
function leafTagSegment(name: string | undefined): string | undefined {
  if (!name) return undefined
  const idx = name.lastIndexOf('/')
  return idx >= 0 ? name.slice(idx + 1) : name
}

/**
 * Project the per-`UDTInstance` `{ instance, members }` bundles returned
 * by `getPlantEquipmentDetail` into the `PlantUdtInstance[]` the UI
 * renders. Mirrors {@link extractEquipmentProperties}'s defensive shape
 * checks: we accept anything that has the required `id`/`name` keys and
 * silently drop sentinel `NULL` rows / partially-populated entries.
 *
 * For each member with the parent UDT's `provider_name` set we also
 * pre-compute an Ignition-style pollable path
 * (`[<provider>]<udt.name>/<member.name>`) so the live panel can
 * optionally include the member in its poll list without re-deriving
 * the path on every render.
 */
export function extractUdtInstances(value: unknown): PlantUdtInstance[] {
  if (!Array.isArray(value)) return []
  const out: PlantUdtInstance[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const instanceRaw = extractRawNode(obj['instance'])
    if (!instanceRaw) continue
    const id =
      typeof instanceRaw['id'] === 'string'
        ? (instanceRaw['id'] as string)
        : typeof instanceRaw['_id'] === 'string'
          ? (instanceRaw['_id'] as string)
          : null
    if (!id) continue
    if (seen.has(id)) continue
    seen.add(id)
    const elementId = typeof instanceRaw['_id'] === 'string' ? (instanceRaw['_id'] as string) : id
    const name = typeof instanceRaw['name'] === 'string' ? (instanceRaw['name'] as string) : id
    const providerName =
      typeof instanceRaw['provider_name'] === 'string' && instanceRaw['provider_name']
        ? (instanceRaw['provider_name'] as string)
        : undefined
    const folderName =
      typeof instanceRaw['folder_name'] === 'string' && instanceRaw['folder_name']
        ? (instanceRaw['folder_name'] as string)
        : undefined
    const instanceName =
      typeof instanceRaw['instance_name'] === 'string' && instanceRaw['instance_name']
        ? (instanceRaw['instance_name'] as string)
        : undefined
    const typeId =
      typeof instanceRaw['type_id'] === 'string' && instanceRaw['type_id']
        ? (instanceRaw['type_id'] as string)
        : undefined
    const members = extractUdtMembers(obj['members'], { instanceName: name, providerName })
    const instance: PlantUdtInstance = {
      id,
      _id: elementId,
      name,
      ...extractPlantMeta(instanceRaw),
      members,
      ...(typeId ? { type_id: typeId } : {}),
      ...(providerName ? { provider_name: providerName } : {}),
      ...(folderName ? { folder_name: folderName } : {}),
      ...(instanceName ? { instance_name: instanceName } : {})
    }
    out.push(instance)
  }
  return out
}

function extractUdtMembers(
  value: unknown,
  context: { instanceName: string; providerName: string | undefined }
): PlantUdtMember[] {
  if (!Array.isArray(value)) return []
  const out: PlantUdtMember[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const raw = item as Record<string, unknown>
    const id =
      typeof raw['id'] === 'string'
        ? (raw['id'] as string)
        : typeof raw['_id'] === 'string'
          ? (raw['_id'] as string)
          : null
    if (!id) continue
    if (seen.has(id)) continue
    seen.add(id)
    const elementId = typeof raw['_id'] === 'string' ? (raw['_id'] as string) : id
    const name = typeof raw['name'] === 'string' ? (raw['name'] as string) : id
    const memberType =
      typeof raw['member_type'] === 'string' && raw['member_type']
        ? (raw['member_type'] as string)
        : undefined
    const memberValue = stringifyMemberValue(raw['value'])
    const dataType =
      typeof raw['data_type'] === 'string' && raw['data_type']
        ? (raw['data_type'] as string)
        : undefined
    const engUnit =
      typeof raw['eng_unit'] === 'string' && raw['eng_unit']
        ? (raw['eng_unit'] as string)
        : undefined
    // Prefer the ingest-supplied `tag_path` on the node itself: post-UDT-
    // refactor each UDT-instance member :Tag carries its canonical
    // `[Provider]InstanceFullPath/MemberPath` so the live panel doesn't
    // have to recompose it (and so nested members whose in-instance path
    // has more than one segment — `Schedule/CurrentShiftEndDate` — work
    // correctly without a separate ancestor-walk on the read side).
    // Fall back to the legacy composition for older graphs that
    // pre-date the refactor.
    const tagPathRaw =
      typeof raw['tag_path'] === 'string' && raw['tag_path']
        ? (raw['tag_path'] as string)
        : undefined
    const pollPath =
      tagPathRaw ??
      buildUdtMemberPollPath({
        providerName: context.providerName,
        instanceName: context.instanceName,
        memberName: name
      })
    const member: PlantUdtMember = {
      id,
      _id: elementId,
      name,
      ...(memberType ? { member_type: memberType } : {}),
      ...(memberValue !== undefined ? { value: memberValue } : {}),
      ...(dataType ? { data_type: dataType } : {}),
      ...(engUnit ? { eng_unit: engUnit } : {}),
      ...(pollPath ? { pollPath } : {})
    }
    out.push(member)
  }
  return out
}

/**
 * UDTMember `value` is whatever the Ignition serializer last captured —
 * could be a primitive, a JSON-encoded dataset, `[object Object]`, etc.
 * For the UI we keep numbers/booleans/strings as their `String()`
 * representation; objects collapse to `'[object …]'` so the table
 * doesn't blow up rendering a `Dataset`. Empty / nullish values return
 * undefined so the caller can omit the field.
 */
function stringifyMemberValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'object') return '[object]'
  return String(value)
}

/**
 * Build the Ignition tag path used by the proxy's `/live-data/tags`
 * handler for a UDT-member leaf reading. Mirrors the shape produced by
 * `tagPathFor` in `LiveTagPanel`: `[<provider>]<udt.name>/<member>`.
 * Returns `undefined` when we don't know which provider owns the UDT
 * instance (the gateway will refuse a bare path).
 */
function buildUdtMemberPollPath(input: {
  providerName: string | undefined
  instanceName: string
  memberName: string
}): string | undefined {
  if (!input.providerName) return undefined
  if (!input.instanceName || !input.memberName) return undefined
  return `[${input.providerName}]${input.instanceName}/${input.memberName}`
}

function pickNumeric(record: Record<string, unknown>, keys: string[]): number | undefined {
  for (const key of keys) {
    const v = record[key]
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (typeof v === 'string') {
      const n = Number.parseFloat(v)
      if (Number.isFinite(n)) return n
    }
  }
  return undefined
}

/**
 * Walk a `PlantEquipmentProperty[]` and lift out the unique tags wired
 * to those properties so callers (today: `EquipmentDetailPage`'s
 * `LiveTagPanel`) can poll live values without us having to also
 * `collect` tags as a separate column in the cypher.
 */
export function collectTagsFromProperties(properties: PlantEquipmentProperty[]): PlantTag[] {
  const seen = new Set<string>()
  const tags: PlantTag[] = []
  for (const prop of properties) {
    if (!prop.tag) continue
    if (seen.has(prop.tag.id)) continue
    seen.add(prop.tag.id)
    tags.push(prop.tag)
  }
  return tags
}

/**
 * Merge one or more tag lists into a single list deduped by tag `id`,
 * preserving first-seen order. Used to fold controller-mediated SCADA
 * tags (traced Equipment -> Controller -> PLCTag -> Tag) into the
 * property-mediated tag set without double-listing a tag reachable
 * both ways.
 */
export function mergeTagsById(...lists: PlantTag[][]): PlantTag[] {
  const seen = new Set<string>()
  const tags: PlantTag[] = []
  for (const list of lists) {
    for (const tag of list) {
      if (!tag || seen.has(tag.id)) continue
      seen.add(tag.id)
      tags.push(tag)
    }
  }
  return tags
}
